import { createSignal, createEffect, For, Show, untrack } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import Text from '../components/Text';
import Modal from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { SkeletonCard } from '../components/Skeleton';
import ResourceAccessPanel from '../components/ResourceAccessPanel';
import PlayerSearchPicker from '../components/PlayerSearchPicker';
import { EntryNoteButton } from '../components/PlayerNameEntryList';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useToast } from '../components/MessageBox';
import { showConfirm, showDialog } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { can, PLAYERLIST_ACTIONS, playerListAccessLabelKey } from '../services/resourceActions';
import { downloadFromApi } from '../services/downloadFile';
import { getPlayerListExportEntriesUrl } from '../api/generated/player-list';
import type { PlayerListAddEntryRequest, PlayerListDto, PlayerListEntryDto, PlayerListImportResultDto } from '../api';
import styles from './PlayerLists.module.css';

function accessBadgeClass(styles: Record<string, string>, actions: readonly string[]): string {
    if (can(actions, PLAYERLIST_ACTIONS.Delete)) return styles.badgeOwner;
    if (can(actions, PLAYERLIST_ACTIONS.EntriesWrite)) return styles.badgeContributor;
    return styles.badgeReader;
}

type DetailTab = 'members' | 'access';

export default function PlayerLists() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [lists, setLists] = createStore<PlayerListDto[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [creating, setCreating] = createSignal(false);

    const [detailId, setDetailId] = createSignal<string | null>(null);
    const [detailTab, setDetailTab] = createSignal<DetailTab>('members');
    const [entries, setEntries] = createStore<PlayerListEntryDto[]>([]);
    const [entriesLoading, setEntriesLoading] = createSignal(false);
    const [memberBusy, setMemberBusy] = createSignal(false);

    const [exporting, setExporting] = createSignal(false);
    const [importing, setImporting] = createSignal(false);
    const [pendingImportFile, setPendingImportFile] = createSignal<File | null>(null);
    const [importPreview, setImportPreview] = createSignal<PlayerListImportResultDto | null>(null);
    let importInput: HTMLInputElement | undefined;

    const detailList = () => lists.find((l) => l.id === detailId()) ?? null;

    const loadLists = async () => {
        const res = await api.playerList.listPlayerLists();
        if (isOk(res.data)) setLists(reconcile(res.data.value.lists ?? [], { key: 'id' }));
        else toast?.error(firstError(res.data) ?? t('PLAYERLIST_TOAST_FAILED'));
        setLoading(false);
    };

    createEffect(() => {
        untrack(() => { void loadLists(); });
    });

    const createList = async () => {
        const result = await showDialog({
            title: t('PLAYERLIST_CREATE_TITLE'),
            fields: [{ key: 'name', label: t('PLAYERLIST_NAME'), type: 'text', placeholder: t('PLAYERLIST_NAME_PLACEHOLDER') }],
        });
        const name = result?.name?.trim();
        if (!name) return;
        setCreating(true);
        const res = await api.playerList.createPlayerList({ name });
        setCreating(false);
        if (isOk(res.data)) {
            await loadLists();
            toast?.success(t('PLAYERLIST_CREATED'));
        } else {
            toast?.error(firstError(res.data) ?? t('PLAYERLIST_TOAST_FAILED'));
        }
    };

    const renameList = async (list: PlayerListDto) => {
        const result = await showDialog({
            title: t('PLAYERLIST_RENAME_TITLE'),
            fields: [{ key: 'name', label: t('PLAYERLIST_NAME'), type: 'text', defaultValue: list.name }],
        });
        const name = result?.name?.trim();
        if (!name || name === list.name) return;
        const res = await api.playerList.renamePlayerList(list.id, { name });
        if (isOk(res.data)) await loadLists();
        else toast?.error(firstError(res.data) ?? t('PLAYERLIST_TOAST_FAILED'));
    };

    const deleteList = async (list: PlayerListDto) => {
        if (!await showConfirm(t('PLAYERLIST_CONFIRM_DELETE', list.name), true)) return;
        const res = await api.playerList.deletePlayerList(list.id);
        if (isOk(res.data)) {
            if (detailId() === list.id) setDetailId(null);
            await loadLists();
        } else {
            toast?.error(firstError(res.data) ?? t('PLAYERLIST_TOAST_FAILED'));
        }
    };

    const openDetail = async (list: PlayerListDto) => {
        setDetailId(list.id);
        setDetailTab('members');
        await loadEntries(list.id);
    };

    const loadEntries = async (listId: string) => {
        setEntriesLoading(true);
        const res = await api.playerList.getEntries(listId);
        if (isOk(res.data)) setEntries(reconcile(res.data.value.entries ?? [], { key: 'id' }));
        else toast?.error(firstError(res.data) ?? t('PLAYERLIST_TOAST_FAILED'));
        setEntriesLoading(false);
    };

    const addMember = async (player: PlayerListAddEntryRequest) => {
        const id = detailId();
        if (!id) return false;
        setMemberBusy(true);
        try {
            const res = await api.playerList.addEntry(id, player);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('PLAYERLIST_TOAST_FAILED'));
                return false;
            }
            await loadEntries(id);
            await loadLists();
            return true;
        } catch {
            toast?.error(t('PLAYERLIST_TOAST_FAILED'));
            return false;
        } finally {
            setMemberBusy(false);
        }
    };

    const removeMember = async (entry: PlayerListEntryDto) => {
        const id = detailId();
        if (!id) return;
        if (!await showConfirm(t('PLAYERLIST_CONFIRM_REMOVE_MEMBER', entry.currentUsername), true)) return;
        setMemberBusy(true);
        try {
            const res = await api.playerList.removeEntry(id, entry.id);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('PLAYERLIST_TOAST_FAILED'));
                return;
            }
            await loadEntries(id);
            await loadLists();
        } catch {
            toast?.error(t('PLAYERLIST_TOAST_FAILED'));
        } finally {
            setMemberBusy(false);
        }
    };

    const setMemberNote = async (entry: PlayerListEntryDto, note: string) => {
        const id = detailId();
        if (!id) return;
        setMemberBusy(true);
        try {
            const res = await api.playerList.setEntryNote(id, entry.id, { note });
            if (isOk(res.data)) await loadEntries(id);
            else toast?.error(firstError(res.data) ?? t('PLAYERLIST_TOAST_FAILED'));
        } catch (e: any) {
            toast?.error(e?.message ?? t('PLAYERLIST_TOAST_FAILED'));
        } finally {
            setMemberBusy(false);
        }
    };

    const exportEntries = async () => {
        const id = detailId();
        const list = detailList();
        if (!id || !list) return;
        setExporting(true);
        const failed = await downloadFromApi(getPlayerListExportEntriesUrl(id), `spielerliste-${list.name}.xlsx`);
        setExporting(false);
        if (failed) toast?.error(t('PLAYERLIST_EXPORT_FAILED'));
    };

    const importChangeCount = () => (importPreview()?.added ?? 0) + (importPreview()?.notesUpdated ?? 0);

    const closeImport = () => { setImportPreview(null); setPendingImportFile(null); };

    const runImport = async (dryRun: boolean) => {
        const id = detailId();
        const file = pendingImportFile();
        if (!id || !file) return;
        setImporting(true);
        try {
            const res = await api.playerList.importEntries(id, { file, dryRun });
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('PLAYERLIST_IMPORT_FAILED'));
                closeImport();
                return;
            }
            const result = res.data.value;
            if (result.applied) {
                closeImport();
                toast?.success(t('PLAYERLIST_IMPORT_APPLIED', result.added, result.notesUpdated));
                await loadEntries(id);
                await loadLists();
                return;
            }
            setImportPreview(result);
        } finally {
            setImporting(false);
        }
    };

    const applyImport = async () => {
        if (!await showConfirm(t('PLAYERLIST_IMPORT_CONFIRM', importPreview()?.added ?? 0, importPreview()?.notesUpdated ?? 0), true,
            { confirmLabel: t('PLAYERLIST_IMPORT_APPLY'), cancelLabel: t('COMMON_CANCEL') })) return;
        await runImport(false);
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><i class="fa-solid fa-users-rectangle" style={{ 'margin-right': 'var(--spacing-sm)' }} /><Text val="PLAYERLIST_TITLE" /></h1>
                <button class="btn btn-primary" disabled={creating()} onClick={() => void createList()}>
                    <Show when={creating()} fallback={<><i class="fa-solid fa-plus" /> <Text val="PLAYERLIST_NEW" /></>}>
                        <i class="fa-solid fa-spinner fa-spin" />
                    </Show>
                </button>
            </div>
            <p class={styles.hint}><Text val="PLAYERLIST_HELP" /></p>

            <Show when={!loading()} fallback={
                <div class={styles.grid}><For each={[1, 2, 3]}>{() => <SkeletonCard />}</For></div>
            }>
                <Show when={lists.length > 0} fallback={
                    <EmptyState icon="fa-users-rectangle" title={t('PLAYERLIST_EMPTY_TITLE')} hint={t('PLAYERLIST_EMPTY_HINT')} />
                }>
                    <div class={styles.grid}>
                        <For each={lists}>
                            {(list) => (
                                <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                                    <div class={styles.cardHeader}>
                                        <span class={styles.cardName}>{list.name}</span>
                                        <span class={`${styles.badge} ${accessBadgeClass(styles, list.myActions)}`}>
                                            <Text val={playerListAccessLabelKey(list.myActions)} />
                                        </span>
                                    </div>
                                    <span class={styles.cardMeta}>{t('PLAYERLIST_MEMBER_COUNT', list.memberCount)}</span>
                                    <span class={styles.cardMeta}>{t('PLAYERLIST_CREATED_AT', formatDateTime(list.createdAt))}</span>
                                    <div class={styles.cardActions}>
                                        <button class="btn btn-secondary btn-sm" onClick={() => void openDetail(list)}>
                                            <i class="fa-solid fa-users" /> <Text val="PLAYERLIST_MANAGE" />
                                        </button>
                                        <Show when={can(list.myActions, PLAYERLIST_ACTIONS.ConfigWrite)}>
                                            <button class="btn btn-secondary btn-sm" title={t('PLAYERLIST_RENAME_TITLE')} onClick={() => void renameList(list)}>
                                                <i class="fa-solid fa-pen-to-square" />
                                            </button>
                                        </Show>
                                        <Show when={can(list.myActions, PLAYERLIST_ACTIONS.Delete)}>
                                            <button class="btn btn-icon btn-danger btn-sm" title={t('PLAYERLIST_DELETE_TITLE')} onClick={() => void deleteList(list)}>
                                                <i class="fa-solid fa-trash" />
                                            </button>
                                        </Show>
                                    </div>
                                </div>
                            )}
                        </For>
                    </div>
                </Show>
            </Show>

            <Modal open={detailId() != null} onClose={() => setDetailId(null)} title={detailList()?.name}
                maxWidth="52rem" busy={memberBusy()}>
                <Show when={detailList()}>
                    {(list) => (
                        <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                            <div class={styles.tabBar}>
                                <button class={`${styles.tab} ${detailTab() === 'members' ? styles.tabActive : ''}`}
                                    onClick={() => setDetailTab('members')}>
                                    <i class="fa-solid fa-users" /> <Text val="PLAYERLIST_TAB_MEMBERS" />
                                </button>
                                <Show when={can(list().myActions, PLAYERLIST_ACTIONS.AccessRead)}>
                                    <button class={`${styles.tab} ${detailTab() === 'access' ? styles.tabActive : ''}`}
                                        onClick={() => setDetailTab('access')}>
                                        <i class="fa-solid fa-user-lock" /> <Text val="PLAYERLIST_TAB_ACCESS" />
                                    </button>
                                </Show>
                            </div>

                            <Show when={detailTab() === 'members'}>
                                <Show when={can(list().myActions, PLAYERLIST_ACTIONS.EntriesWrite)}>
                                    <PlayerSearchPicker onSelect={addMember} />
                                </Show>

                                <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                                    <button type="button" class="btn btn-secondary btn-sm" disabled={exporting()}
                                        title={t('PLAYERLIST_EXPORT_HELP')} onClick={() => void exportEntries()}>
                                        <Show when={exporting()} fallback={<><i class="fa-solid fa-file-arrow-down" /> <Text val="PLAYERLIST_EXPORT" /></>}>
                                            <i class="fa-solid fa-spinner fa-spin" />
                                        </Show>
                                    </button>
                                    <Show when={can(list().myActions, PLAYERLIST_ACTIONS.EntriesWrite)}>
                                        <button type="button" class="btn btn-secondary btn-sm" disabled={importing()}
                                            title={t('PLAYERLIST_IMPORT_HELP')} onClick={() => importInput?.click()}>
                                            <i class="fa-solid fa-file-arrow-up" /> <Text val="PLAYERLIST_IMPORT" />
                                        </button>
                                        <input ref={importInput} type="file" accept=".xlsx" style={{ display: 'none' }}
                                            onChange={(e) => {
                                                const file = e.currentTarget.files?.[0];
                                                e.currentTarget.value = '';
                                                if (!file) return;
                                                setPendingImportFile(file);
                                                void runImport(true);
                                            }} />
                                    </Show>
                                </div>

                                <Show when={!entriesLoading()} fallback={
                                    <i class={`fa-solid fa-spinner fa-spin fa-2x ${styles.entriesLoading}`} />
                                }>
                                    <Show when={entries.length > 0} fallback={
                                        <div class={styles.emptySmall}><i class="fa-solid fa-user-slash" /><p><Text val="PLAYERLIST_NO_MEMBERS" /></p></div>
                                    }>
                                        <div class={styles.memberFeed}>
                                            <For each={entries}>
                                                {(entry) => (
                                                    <div class={styles.memberRow}>
                                                        <img src={`https://mc-heads.net/avatar/${entry.uuid}/24`} alt="" class={styles.memberAvatar} />
                                                        <div class={styles.memberMain}>
                                                            <span class={styles.memberName}>{entry.currentUsername}</span>
                                                            <Show when={entry.note}><span class={styles.memberNote}>{entry.note}</span></Show>
                                                        </div>
                                                        <span class={styles.memberUuid}>{entry.uuid}</span>
                                                        <span class={styles.memberTime}>{formatDateTime(entry.addedAt)}</span>
                                                        <Show when={can(list().myActions, PLAYERLIST_ACTIONS.EntriesWrite)}>
                                                            <EntryNoteButton playerName={entry.currentUsername} note={entry.note}
                                                                onSave={(note) => setMemberNote(entry, note)} />
                                                            <button class="btn btn-icon btn-danger btn-sm" title={t('PLOTACL_REMOVE')}
                                                                onClick={() => void removeMember(entry)}>
                                                                <i class="fa-solid fa-trash" />
                                                            </button>
                                                        </Show>
                                                    </div>
                                                )}
                                            </For>
                                        </div>
                                    </Show>
                                </Show>
                            </Show>

                            <Show when={detailTab() === 'access'}>
                                <ResourceAccessPanel resourceId={list().id} />
                            </Show>
                        </div>
                    )}
                </Show>
            </Modal>

            <Modal open={!!importPreview()} onClose={closeImport} busy={importing()} maxWidth="34rem"
                title={<Text val="PLAYERLIST_IMPORT_PREVIEW_TITLE" />}
                subtitle={<Text val="PLAYERLIST_IMPORT_PREVIEW_HELP" />}>
                <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                    <div class="h" style={{ gap: 'var(--spacing-lg)' }}>
                        <span class={styles.cardMeta}>{t('PLAYERLIST_IMPORT_ADDED', importPreview()?.added ?? 0)}</span>
                        <span class={styles.cardMeta}>{t('PLAYERLIST_IMPORT_ALREADY_MEMBER', importPreview()?.alreadyMember ?? 0)}</span>
                        <span class={styles.cardMeta}>{t('PLAYERLIST_IMPORT_NOTES_UPDATED', importPreview()?.notesUpdated ?? 0)}</span>
                    </div>

                    <Show when={(importPreview()?.issues.length ?? 0) > 0}>
                        <div class="v" style={{ gap: 'var(--spacing-xs)', 'max-height': '18rem', 'overflow-y': 'auto' }}>
                            <span class={styles.cardMeta}>
                                <i class="fa-solid fa-triangle-exclamation" /> <Text val="PLAYERLIST_IMPORT_ISSUES" />
                            </span>
                            <For each={importPreview()?.issues ?? []}>{(issue) => (
                                <div class={styles.cardMeta}>{t('PLAYERLIST_IMPORT_ISSUE_ROW', issue.rowNumber)} {issue.message}</div>
                            )}</For>
                        </div>
                    </Show>

                    <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                        <button type="button" class="btn btn-secondary" onClick={closeImport}>
                            <Text val="COMMON_CANCEL" />
                        </button>
                        <button type="button" class="btn btn-primary" disabled={importing() || importChangeCount() === 0}
                            onClick={() => void applyImport()}>
                            <Text val="PLAYERLIST_IMPORT_APPLY" />
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
